const FEET_PER_METER = 1 / 0.3048;
const MAXIMUM_ICON_COUNT = 500;

/**
 * @typedef {object} ConnectionIconRepeatStyle
 * @property {string} cirm The repeat mode, either 'count' or 'distance'.
 * @property {number} circ The number of icons to place along the line, used when cirm is 'count'.
 * @property {number} cird The distance to place icons apart, in cidu units, used when cirm is 'distance'.
 * @property {string} cidu The unit cird is measured in, either 'feet' or 'meters'.
 * @property {number} d The real world length of the connection, in feet.
 */

/**
 * Compute the icon repeat spacing as a percent of a connection's own length, from whichever repeat mode is configured.
 * @param {ConnectionIconRepeatStyle} connectionStyle
 * @returns {number} The repeat spacing as a percent of the line, 100 for a style saved before repeat existed, 50 for count mode with no count set since that also places a single icon at the midpoint, or 0 when distance mode is set but not fully configured.
 */
export function GetConnectionIconRepeatPercent(connectionStyle) {
  const { cirm, cidu } = connectionStyle;
  // Style rule inputs come from paper-input elements, so they arrive as strings even though they are numeric.
  const circ = Number(connectionStyle.circ);
  const cird = Number(connectionStyle.cird);
  const connectionLengthInFeet = Number(connectionStyle.d);

  // A style saved before repeat icons existed has no repeat mode set, so keep showing its old single icon at the midpoint
  if (cirm == null) return 100;

  // A count of one or more is rounded to a whole icon and capped to a safe maximum, since both renderers do work proportional to it
  // An unset or zero count defaults to showing a single icon, rather than leaving the line with no icon at all
  if (cirm === 'count') {
    const requestedCount = circ > 0 ? Math.round(circ) : 1;
    const cappedCount = Math.min(requestedCount, MAXIMUM_ICON_COUNT);
    return 100 / (cappedCount + 1);
  }

  // A fixed distance becomes a percent of the connection's own length, floored to the same safe maximum icon count
  if (cirm === 'distance' && cird > 0 && connectionLengthInFeet > 0) {
    const distanceInFeet = cidu === 'meters' ? cird * FEET_PER_METER : cird;
    const percent = (distanceInFeet / connectionLengthInFeet) * 100;
    return Math.max(percent, 100 / (MAXIMUM_ICON_COUNT + 1));
  }

  return 0;
}

/**
 * Compute repeated icon placement points and the local line bearing at each point along a multi vertex polyline.
 * @param {{x: number, y: number}[]} pixelPoints The polyline vertices in pixel space, including any breakpoints.
 * @param {{repeatValue: number, repeatUnit: string}} options The gap between icons, either 'percent' of the total line length or 'pixel' units. No icons are placed when repeatValue is not greater than 0.
 * @returns {{x: number, y: number, bearingDegrees: number}[]} One entry per icon, ordered from the start of the line.
 */
export function GetRepeatingIconPlacementsAlongPolyline(pixelPoints, options) {
  // Do nothing when there are not at least two points to walk along, or no repeat spacing is configured
  if (!Array.isArray(pixelPoints) || pixelPoints.length < 2) return [];
  if (!(options.repeatValue > 0)) return [];

  // Break the polyline into segments so each placement can be resolved to the segment it falls on
  const segmentStartPoints = pixelPoints.slice(0, -1);
  const segments = segmentStartPoints.map((point, index) => buildSegment(point, pixelPoints[index + 1]));
  const totalLength = segments.reduce((sum, segment) => sum + segment.length, 0);
  if (totalLength <= 0) return [];

  // Keep the configured gap exact, falling back to one icon at the midpoint when the gap does not fit even once
  const repeatDistance = options.repeatUnit === 'pixel' ? options.repeatValue : (options.repeatValue / 100) * totalLength;
  if (repeatDistance >= totalLength) return [resolvePlacement(segments, totalLength / 2)];
  const distances = [];
  for (let multiple = 1; multiple * repeatDistance < totalLength; multiple++) {
    distances.push(multiple * repeatDistance);
  }

  // Resolve each distance to a point and the bearing of the segment it falls on
  return distances.map((distance) => resolvePlacement(segments, distance));
}

/**
 * Compute repeated icon placement points and bearings along a polyline for a connection's own repeat style.
 * @param {{x: number, y: number}[]} pixelPoints The polyline vertices in pixel space, including any breakpoints.
 * @param {ConnectionIconRepeatStyle} connectionStyle
 * @returns {{x: number, y: number, bearingDegrees: number}[]} One entry per icon, ordered from the start of the line.
 */
export function GetRepeatingConnectionIconPlacements(pixelPoints, connectionStyle) {
  const repeatPercent = GetConnectionIconRepeatPercent(connectionStyle);
  return GetRepeatingIconPlacementsAlongPolyline(pixelPoints, { repeatValue: repeatPercent, repeatUnit: 'percent' });
}

// Build one segment's endpoints, length, and bearing in degrees
function buildSegment(start, end) {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  const bearingDegrees = (Math.atan2(deltaY, deltaX) * 180) / Math.PI;
  return { start, end, length, bearingDegrees };
}

// Walk the segments to find the point and bearing at a given distance along the line
function resolvePlacement(segments, distance) {
  let remainingDistance = distance;
  for (const segment of segments) {
    const isLastSegment = segment === segments[segments.length - 1];
    if (remainingDistance <= segment.length || isLastSegment) {
      const fractionOfSegment = segment.length > 0 ? Math.min(1, remainingDistance / segment.length) : 0;
      return {
        x: segment.start.x + (segment.end.x - segment.start.x) * fractionOfSegment,
        y: segment.start.y + (segment.end.y - segment.start.y) * fractionOfSegment,
        bearingDegrees: segment.bearingDegrees
      };
    }
    remainingDistance -= segment.length;
  }
  // Every caller in this file guarantees at least one segment, so the loop above always returns before reaching here
  return { x: segments[0].start.x, y: segments[0].start.y, bearingDegrees: segments[0].bearingDegrees };
}

/**
 * Check whether an SVG path traces a plain rectangle spanning the full given width and height, regardless of styling.
 * @param {string} pathData The SVG path's d attribute value.
 * @param {number} width
 * @param {number} height
 * @returns {boolean} True when the path traces a plain rectangle filling the canvas.
 */
export function CheckPathIsFullCanvasRectangle(pathData, width, height) {
  if (!pathData) return false;

  // MLHVZ are the SVG moveto, lineto, horizontal, vertical, and closepath commands, so reject anything else such as a curve or an arc
  const commandLetters = pathData.match(/[a-zA-Z]/g) ?? [];
  if (commandLetters.some((letter) => !'MLHVZmlhvz'.includes(letter))) return false;

  // A rectangle outline is drawn as a single closed subpath with exactly four distinct corners
  const { points, subpathCount } = walkPathIntoPointsAndSubpathCount(pathData);
  const corners = removeClosingDuplicatePoint(points);
  if (subpathCount !== 1 || corners.length !== 4) return false;

  // The four corners must be exactly the canvas's own corners, connected only by straight axis-aligned edges
  const cornerKeys = new Set(corners.map((corner) => `${corner.x},${corner.y}`));
  const expectedCornerKeys = [`0,0`, `${width},0`, `${width},${height}`, `0,${height}`];
  const hasExactCanvasCorners = cornerKeys.size === 4 && expectedCornerKeys.every((key) => cornerKeys.has(key));
  const hasOnlyAxisAlignedEdges = corners.every((corner, index) => {
    const nextCorner = corners[(index + 1) % corners.length];
    return (corner.x === nextCorner.x) !== (corner.y === nextCorner.y);
  });

  return hasExactCanvasCorners && hasOnlyAxisAlignedEdges;
}

// Walk a straight line SVG path, listing every absolute point it visits and how many separate subpaths it has
function walkPathIntoPointsAndSubpathCount(pathData) {
  const commandPattern = /([MLHVZmlhvz])([^MLHVZmlhvz]*)/g;
  const points = [];
  let subpathCount = 0;
  let currentX = 0;
  let currentY = 0;
  let match;

  while ((match = commandPattern.exec(pathData))) {
    const command = match[1];
    const commandArguments = match[2]
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    const isRelative = command === command.toLowerCase();
    const commandType = command.toUpperCase();

    if (commandType === 'M') subpathCount++;

    if (commandType === 'M' || commandType === 'L') {
      for (let argumentIndex = 0; argumentIndex < commandArguments.length; argumentIndex += 2) {
        currentX = isRelative ? currentX + commandArguments[argumentIndex] : commandArguments[argumentIndex];
        currentY = isRelative ? currentY + commandArguments[argumentIndex + 1] : commandArguments[argumentIndex + 1];
        points.push({ x: currentX, y: currentY });
      }
    } else if (commandType === 'H') {
      for (const value of commandArguments) {
        currentX = isRelative ? currentX + value : value;
        points.push({ x: currentX, y: currentY });
      }
    } else if (commandType === 'V') {
      for (const value of commandArguments) {
        currentY = isRelative ? currentY + value : value;
        points.push({ x: currentX, y: currentY });
      }
    }
    // A close command returns to the already recorded start point, so it adds no new point
  }

  return { points, subpathCount };
}

// Drop the final point when it only repeats an explicit close back to the path's own starting corner
function removeClosingDuplicatePoint(points) {
  const firstPoint = points[0];
  const lastPoint = points.at(-1);
  const isClosingDuplicate = points.length > 1 && firstPoint.x === lastPoint.x && firstPoint.y === lastPoint.y;
  return isClosingDuplicate ? points.slice(0, -1) : points;
}
